import { ruleForBucket, type ScoreValue } from '@/components/ui/scoreScale';
import { RECOMMENDATIONS, type Recommendation } from '@/api/vocabularies';

/**
 * §6.6's two vocabularies **as the form holds them** — the five points of §3's score scale and the
 * four verdicts — plus the one product rule that reads off the first of them (a comment is required
 * at 1, 2 and 5).
 *
 * ⚠️ **Both are derived, neither is restated.** The five points come out of `ruleForBucket`, which is
 * where §3's *"The score scale (1–5)"* table lives (`components/ui/scoreScale.ts`), and the four
 * verdicts come out of `RECOMMENDATIONS`, which comes out of the OpenAPI document
 * (`api/vocabularies.ts`). A sixth point added to the scale, or a fifth verdict added on the backend,
 * reaches this form without an edit — and `fillingIn.test.tsx` pins the derivation itself, so a
 * hand-written `[1, 2, 3, 4, 5]` replacing either would be a visible change rather than a quiet one.
 *
 * ⚠️ **Everything here is a *choice in a control*, not a payload value.** A radio's value is a string
 * and *nothing chosen* is the empty string, so `rating: number | null` from the API becomes
 * `RatingChoice` here and back again at the edge (`fillingInFields.ts`). The labels are **not** here:
 * they live in `components.json` beside every other score and verdict word, and the component asks for
 * them with its own `t` — exactly as `Score` and `StatusDot` do, so no screen can invent a label §3
 * and §9 already fix.
 */

/**
 * The five points, in §3's order, read off §3's own table.
 *
 * `Object.keys` returns integer-like keys in ascending numeric order (ES2015 §9.1.11.1), so the order
 * is the table's rather than a coincidence — and the cast is the shape `api/vocabularies.ts` uses for
 * every derived list in the app.
 */
export const SCORE_VALUES = Object.keys(ruleForBucket).map(Number) as ScoreValue[];

/**
 * What a control holds while nothing has been chosen — **Radix's own value for that state**, not a
 * sentinel of ours: a `RadioGroup` whose `value` matches no item has nothing checked, and the empty
 * string is what keeps it controlled from its first render (the finding `QuestionDialog` records for
 * `Select`, measured in a live browser by TASK-078's pass).
 *
 * It never reaches a request: an unrated question is a score nobody rated, and the autosave omits the
 * field rather than sending a `null` — which is a 422 by design (`api/interviews.ts`).
 */
export const NOTHING_CHOSEN = '';

/** A rating as the scale holds it: one of the five as a string, or nothing chosen. */
export type RatingChoice = typeof NOTHING_CHOSEN | `${ScoreValue}`;

/** The scale's own values, with *nothing chosen* first — the tuple `z.enum` needs. */
export const RATING_CHOICES = [NOTHING_CHOSEN, ...SCORE_VALUES.map(String)] as [
  RatingChoice,
  ...RatingChoice[],
];

/** A verdict as the Overall block holds it: one of the four, or nothing chosen. */
export type VerdictChoice = typeof NOTHING_CHOSEN | Recommendation;

/** The four verdicts, with *nothing chosen* first — the same tuple, for the same reason. */
export const VERDICT_CHOICES = [NOTHING_CHOSEN, ...RECOMMENDATIONS] as [
  VerdictChoice,
  ...VerdictChoice[],
];

/**
 * Whether a string coming back out of a Radix control is a rating this form can hold.
 *
 * The same device `isCategory` and `isRecommendation` are: a value this screen cannot name is one it
 * does not store, so nothing here is cast on the way in from the DOM.
 */
export const isRatingChoice = (value: string): value is RatingChoice =>
  (RATING_CHOICES as string[]).includes(value);

/**
 * The choice as the **score** it stands for, or `null` while nothing is chosen.
 *
 * §6.6's margin mark renders a rating the way §6.7 will render it once this is submitted, and a rating
 * is a number there — *nothing chosen* is the em dash §3 fixes for *no score at all*, never a `0`. This
 * is the same edge crossing `fillingInFields.ts` performs for the payload, in the one other direction
 * the screen needs it.
 */
export const ratingChosen = (rating: RatingChoice): ScoreValue | null =>
  rating === NOTHING_CHOSEN ? null : (Number(rating) as ScoreValue);

/**
 * ⚠️ **The three ratings a comment is required for (§6.6), and the requirement is *revealed*, never
 * enforced here.** *"A 5 with no explanation is as useless six months later as a 1 with none"* — and
 * §6.6 is equally explicit that it is *"enforced on submit, not on every keystroke, so nobody is
 * nagged mid-thought"*. So this list decides what the screen **says**; the gate that refuses a
 * submission is TASK-084's, and the autosave accepts a 1 with no comment by design (ADR 0007 — a
 * draft is whatever has been written so far).
 *
 * There is no derivation available for these three: it is a product rule about which scores need
 * explaining, written in §6.6 and nowhere in the API.
 */
export const COMMENT_REQUIRED_AT: RatingChoice[] = ['1', '2', '5'];

/** Whether the comment beside this rating is the one §6.6 asks for a sentence with. */
export const commentIsRequired = (rating: RatingChoice): boolean =>
  COMMENT_REQUIRED_AT.includes(rating);
